import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { Link, LinkProps } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { forwardRef } from 'react';
import {
  entityRouteRef,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { Box, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';

export type EntityRefLinkClasses = {
  icon?: string;
};

export type EntityRefLinkProps = {
  entityRef: Entity | CompoundEntityRef | string;
  defaultKind?: string;
  defaultNamespace?: string;
  hideIcon?: boolean;
  classes?: EntityRefLinkClasses;
} & Omit<LinkProps, 'to'>;

const useStyles = makeStyles(
  (theme: Theme) => ({
    icon: {
      marginRight: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      '& svg': {
        verticalAlign: 'middle',
      },
    },
  }),
  { name: 'CatalogReactEntityDisplayName' },
);

export const EntityRefLink = forwardRef<any, EntityRefLinkProps>(
  (props, ref) => {
    const {
      entityRef,
      defaultKind,
      defaultNamespace,
      title,
      hideIcon,
      classes: classesProp,
      ...linkProps
    } = props;
    const classes = useStyles();
    const entityRoute = useEntityRoute(props.entityRef);

    const { primaryTitle, Icon } = useEntityPresentation(entityRef, {
      defaultKind,
      defaultNamespace,
    });

    return (
      <Link
        {...linkProps}
        underline="always"
        ref={ref}
        target="_blank"
        to={entityRoute}
        onClick={e => {
          // Stop the click event from propagating to parent elements, which is a problem when this component
          // for example is used inside a clickable table row (we don't want the row's onClick to be triggered when clicking the link).
          e.stopPropagation();
          linkProps.onClick?.(e);
        }}
      >
        {Icon && !hideIcon ? (
          <Box
            component="span"
            className={clsx(classes.icon, classesProp?.icon)}
          >
            <Icon fontSize="inherit" />
          </Box>
        ) : null}
        {primaryTitle}
      </Link>
    );
  },
) as (props: EntityRefLinkProps) => JSX.Element;

// Hook that computes the route to a given entity / ref. This is a bit
// contrived, because it tries to retain the casing of the entity name if
// present, but not of other parts. This is in an attempt to make slightly more
// nice-looking URLs.
function useEntityRoute(
  entityRef: Entity | CompoundEntityRef | string,
): string {
  const entityRoute = useRouteRef(entityRouteRef);

  let kind;
  let namespace;
  let name;

  if (typeof entityRef === 'string') {
    const parsed = parseEntityRef(entityRef);
    kind = parsed.kind;
    namespace = parsed.namespace;
    name = parsed.name;
  } else if ('metadata' in entityRef) {
    kind = entityRef.kind;
    namespace = entityRef.metadata.namespace;
    name = entityRef.metadata.name;
  } else {
    kind = entityRef.kind;
    namespace = entityRef.namespace;
    name = entityRef.name;
  }

  kind = kind.toLocaleLowerCase('en-US');
  namespace = namespace?.toLocaleLowerCase('en-US') ?? DEFAULT_NAMESPACE;

  const routeParams = {
    kind: encodeURIComponent(kind),
    namespace: encodeURIComponent(namespace),
    name: encodeURIComponent(name),
  };

  return entityRoute(routeParams);
}
